import type { TAsyncState, TStateCreator } from "../types";
import {
    initialAsyncState,
    pendingAsyncState,
    fulfilledAsyncState,
    rejectedAsyncState,
} from "../types";

/**
 * Yardi instance a delivery/threshold applies to (live is throttled separately)
 */
export type TSftpDeliveryInstance = "replica" | "live";

/**
 * File processing threshold window (times are UTC "HH:MM:SS")
 */
export type TFileProcessingThreshold = {
    id: string;
    companyId: string;
    startTime: string;
    endTime: string;
    threshold: number;
    instance: TSftpDeliveryInstance;
    createdAt: string;
    updatedAt: string;
};

/**
 * Company SFTP connection and delivery parameters (Proda admin only).
 * Secrets are write-only — the API exposes only has* presence flags.
 */
export type TCompanySftpSettings = {
    id: string;
    companyId: string;
    enabled: boolean;
    remoteDirectory: string;
    fileNamePattern: string;
    notes: string | null;
    host: string;
    port: number;
    username: string;
    knownHosts: string;
    hasPassword: boolean;
    hasPrivateKey: boolean;
    hasPrivateKeyPassphrase: boolean;
    passwordSha256: string | null;
    privateKeySha256: string | null;
    privateKeyPassphraseSha256: string | null;
    createdAt: string;
    updatedAt: string;
};

/**
 * Environment-level SFTP defaults that blank company fields fall back to.
 */
export type TSftpEnvironmentDefaults = {
    host: string;
    port: number;
    username: string;
    hasPassword: boolean;
    hasPrivateKey: boolean;
    hasPrivateKeyPassphrase: boolean;
    hasKnownHosts: boolean;
    passwordSha256: string | null;
    privateKeySha256: string | null;
    privateKeyPassphraseSha256: string | null;
};

/**
 * SFTP submission delivery stage
 */
export type TSftpSubmissionStatus =
    | "queued"
    | "waiting_for_window"
    | "uploading"
    | "uploaded"
    | "failed"
    | "processed"
    | "rejected";

/**
 * One stage change in a submission's delivery history
 */
export type TSftpSubmissionEvent = {
    status: TSftpSubmissionStatus;
    detail: string | null;
    occurredAt: string;
};

/**
 * One SFTP upload attempt linked to a submission
 */
export type TSftpSubmissionTransfer = {
    id: string;
    status: "in_progress" | "success" | "failed";
    remotePath: string;
    sizeBytes: number | null;
    durationMs: number | null;
    errorMessage: string | null;
    startedAt: string;
    completedAt: string | null;
    hasContent: boolean;
};

/**
 * Company's SnapLogic Yardi master-data connection (Proda admin only).
 * The bearer token is write-only — the API exposes only a has* presence flag.
 */
export type TCompanyYardiMasterDataSettings = {
    id: string;
    companyId: string;
    baseUrl: string;
    hasBearerToken: boolean;
    bearerTokenSha256: string | null;
    createdAt: string;
    updatedAt: string;
};

/**
 * One Yardi master-data sync run attempt (SnapLogic object map ingestion)
 */
export type TYardiMasterDataSyncRun = {
    id: string;
    status: "running" | "succeeded" | "failed";
    trigger: "manual" | "scheduled";
    startedAt: string;
    finishedAt: string | null;
    error: string | null;
    objectCounts: Record<string, number> | null;
};

/**
 * A file's SFTP delivery activity (current stage, context, history)
 */
export type TSftpFileSubmission = {
    id: string;
    financialFileId: string;
    fileName: string | null;
    fileType: string | null;
    periodLabel: string | null;
    totalLineItems: number | null;
    uniqueKey: string;
    instance: TSftpDeliveryInstance;
    status: TSftpSubmissionStatus;
    errorMessage: string | null;
    triggeredBy: string | null;
    triggeredAt: string;
    updatedAt: string;
    events: TSftpSubmissionEvent[];
    transfers: TSftpSubmissionTransfer[];
};

/**
 * Company settings slice state
 */
export type TCompanySettingsState = {
    fileProcessingThresholdsState: TAsyncState<TFileProcessingThreshold[]>;
    // data === null while fulfilled means "not yet configured"
    companySftpSettingsState: TAsyncState<TCompanySftpSettings | null>;
    // Loaded together with the settings; null until first load
    sftpEnvironmentDefaults: TSftpEnvironmentDefaults | null;
    sftpFileSubmissionsState: TAsyncState<TSftpFileSubmission[]>;
    // data === null while fulfilled means "not yet configured"
    companyYardiMasterDataSettingsState: TAsyncState<TCompanyYardiMasterDataSettings | null>;
    yardiMasterDataSyncRunsState: TAsyncState<TYardiMasterDataSyncRun[]>;
};

/**
 * Company settings slice actions
 */
export type TCompanySettingsActions = {
    // File processing thresholds
    setFileProcessingThresholdsLoading: () => void;
    setFileProcessingThresholds: (data: TFileProcessingThreshold[]) => void;
    setFileProcessingThresholdsError: (error: Error) => void;

    // Company SFTP settings
    setCompanySftpSettingsLoading: () => void;
    setCompanySftpSettings: (data: TCompanySftpSettings | null) => void;
    setCompanySftpSettingsError: (error: Error) => void;
    setSftpEnvironmentDefaults: (data: TSftpEnvironmentDefaults | null) => void;

    // SFTP file submissions
    setSftpFileSubmissionsLoading: () => void;
    setSftpFileSubmissions: (data: TSftpFileSubmission[]) => void;
    setSftpFileSubmissionsError: (error: Error) => void;

    // Company Yardi master-data settings
    setCompanyYardiMasterDataSettingsLoading: () => void;
    setCompanyYardiMasterDataSettings: (data: TCompanyYardiMasterDataSettings | null) => void;
    setCompanyYardiMasterDataSettingsError: (error: Error) => void;

    // Yardi master-data sync runs
    setYardiMasterDataSyncRunsLoading: () => void;
    setYardiMasterDataSyncRuns: (data: TYardiMasterDataSyncRun[]) => void;
    setYardiMasterDataSyncRunsError: (error: Error) => void;

    // Reset
    resetCompanySettings: () => void;
};

/**
 * Complete company settings slice type
 */
export type TSliceCompanySettings = TCompanySettingsState & TCompanySettingsActions;

const initialState: TCompanySettingsState = {
    fileProcessingThresholdsState: initialAsyncState(),
    companySftpSettingsState: initialAsyncState(),
    sftpEnvironmentDefaults: null,
    sftpFileSubmissionsState: initialAsyncState(),
    companyYardiMasterDataSettingsState: initialAsyncState(),
    yardiMasterDataSyncRunsState: initialAsyncState(),
};

/**
 * Creates the company settings slice for the store.
 * Handles file processing thresholds and SFTP connection configuration.
 */
export const createSliceCompanySettings = (): TStateCreator<TSliceCompanySettings> => {
    return (set) => ({
        ...initialState,

        // File processing thresholds
        setFileProcessingThresholdsLoading: () => {
            set((state) => ({
                ...state,
                fileProcessingThresholdsState: pendingAsyncState(
                    state.fileProcessingThresholdsState.data,
                ),
            }));
        },

        setFileProcessingThresholds: (data: TFileProcessingThreshold[]) => {
            set({ fileProcessingThresholdsState: fulfilledAsyncState(data) });
        },

        setFileProcessingThresholdsError: (error: Error) => {
            set({ fileProcessingThresholdsState: rejectedAsyncState(error) });
        },

        // Company SFTP settings
        setCompanySftpSettingsLoading: () => {
            set((state) => ({
                ...state,
                companySftpSettingsState: pendingAsyncState(state.companySftpSettingsState.data),
            }));
        },

        setCompanySftpSettings: (data: TCompanySftpSettings | null) => {
            set({ companySftpSettingsState: fulfilledAsyncState(data) });
        },

        setCompanySftpSettingsError: (error: Error) => {
            set({ companySftpSettingsState: rejectedAsyncState(error) });
        },

        setSftpEnvironmentDefaults: (data: TSftpEnvironmentDefaults | null) => {
            set({ sftpEnvironmentDefaults: data });
        },

        // SFTP file submissions
        setSftpFileSubmissionsLoading: () => {
            set((state) => ({
                ...state,
                sftpFileSubmissionsState: pendingAsyncState(state.sftpFileSubmissionsState.data),
            }));
        },

        setSftpFileSubmissions: (data: TSftpFileSubmission[]) => {
            set({ sftpFileSubmissionsState: fulfilledAsyncState(data) });
        },

        setSftpFileSubmissionsError: (error: Error) => {
            set({ sftpFileSubmissionsState: rejectedAsyncState(error) });
        },

        // Company Yardi master-data settings
        setCompanyYardiMasterDataSettingsLoading: () => {
            set((state) => ({
                ...state,
                companyYardiMasterDataSettingsState: pendingAsyncState(
                    state.companyYardiMasterDataSettingsState.data,
                ),
            }));
        },

        setCompanyYardiMasterDataSettings: (data: TCompanyYardiMasterDataSettings | null) => {
            set({ companyYardiMasterDataSettingsState: fulfilledAsyncState(data) });
        },

        setCompanyYardiMasterDataSettingsError: (error: Error) => {
            set({ companyYardiMasterDataSettingsState: rejectedAsyncState(error) });
        },

        // Yardi master-data sync runs
        setYardiMasterDataSyncRunsLoading: () => {
            set((state) => ({
                ...state,
                yardiMasterDataSyncRunsState: pendingAsyncState(
                    state.yardiMasterDataSyncRunsState.data,
                ),
            }));
        },

        setYardiMasterDataSyncRuns: (data: TYardiMasterDataSyncRun[]) => {
            set({ yardiMasterDataSyncRunsState: fulfilledAsyncState(data) });
        },

        setYardiMasterDataSyncRunsError: (error: Error) => {
            set({ yardiMasterDataSyncRunsState: rejectedAsyncState(error) });
        },

        // Reset
        resetCompanySettings: () => {
            set(initialState);
        },
    });
};
